import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import axiosInstance from '../../services/axiosConfig';
import * as XLSX from 'xlsx';
import { Modal, Button, Dropdown } from 'react-bootstrap';

const EventParticipants = () => {
  const { id } = useParams(); // Changed from eventId to id to match route parameter
  const eventId = id; // For backward compatibility with existing code
  const navigate = useNavigate();

  // Core state variables
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [teams, setTeams] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOption, setFilterOption] = useState('all');
  const [groupBy, setGroupBy] = useState('association');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTeamDetailsModal, setShowTeamDetailsModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [newParticipant, setNewParticipant] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'confirmed'
  });
  const [modalError, setModalError] = useState('');
  const [showAssocModal, setShowAssocModal] = useState(false);
  const [selectedAssoc, setSelectedAssoc] = useState('');
  const [assocSearchTerm, setAssocSearchTerm] = useState('');

  // Fetch event details
  const fetchEventDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axiosInstance.get(`/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEvent(response.data.event || response.data);
    } catch (err) {
      console.error('Error fetching event details:', err);
      setError('Could not fetch event details');
    }
  }, [eventId]);

  // Fetch all associations to map IDs to names
  const fetchAllAssociations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      console.log('Fetching associations data...');

      // Get all associations with their details using the new endpoint for properly formatted codes
      const response = await axiosInstance.get('/association-data', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.associations) {
        // console.log('Loaded associations:', response.data.associations);
        if (response.data.associations.length > 0) {
          // console.log('Struttura esempio associazione:', response.data.associations[0]);
        }

        // Check if association_code is missing for any association
        const missingCodes = response.data.associations.filter(a => !a.association_code);
        if (missingCodes.length > 0) {
          console.warn('Some associations are missing association_code:',
            missingCodes.map(a => a.id).join(', '));
          setError('Alcuni dati delle associazioni sono incompleti. Contattare l\'amministratore.');
        }

        // Store associations as-is without fallback values
        setAssociations(response.data.associations);
      } else if (response.data && Array.isArray(response.data)) {
        // In case the API returns an array directly
        console.log('Loaded associations from array:', response.data);
        if (response.data.length > 0) {
          console.log('Struttura esempio associazione (array):', response.data[0]);
        }

        // Check if association_code is missing for any association
        const missingCodes = response.data.filter(a => !a.association_code);
        if (missingCodes.length > 0) {
          console.warn('Some associations are missing association_code:',
            missingCodes.map(a => a.id).join(', '));
          setError('Alcuni dati delle associazioni sono incompleti. Contattare l\'amministratore.');
        }

        // Store associations as-is without fallback values
        setAssociations(response.data);
      } else {
        console.warn('Associations data structure is not as expected:', response.data);
        setError('Impossibile caricare i dati delle associazioni. Formato dati non riconosciuto.');
      }
    } catch (err) {
      console.error('Error fetching associations:', err);
      setError('Impossibile caricare i dati delle associazioni: ' + (err.response?.data?.message || err.message));
    }
  }, []);

  // Fetch participants and teams
  const fetchParticipants = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Get individual participants
      const participantsResponse = await axiosInstance.get(`/api/events/${eventId}/all-participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get teams data
      const teamsResponse = await axiosInstance.get(`/api/events/${eventId}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let participantsData = [];
      if (participantsResponse.data && participantsResponse.data.success) {
        participantsData = participantsResponse.data.participants || [];
        // console.log("API - Partecipanti individuali:", JSON.stringify(participantsResponse.data, null, 2));

        // Log dettagliato del primo partecipante se esiste
        if (participantsData.length > 0) {
          // console.log("DETTAGLIO PRIMO PARTECIPANTE:", JSON.stringify(participantsData[0], null, 2));
          // console.log("CAMPI DISPONIBILI:", Object.keys(participantsData[0]).join(", "));

          // Verifica esplicita dei campi chiave
          // console.log("FISCAL_CODE:", participantsData[0].fiscal_code);
          // console.log("FIRST_NAME:", participantsData[0].first_name);
          // console.log("LAST_NAME:", participantsData[0].last_name);
          // console.log("ASSOCIATION_ID:", participantsData[0].association_id);
        }
      }

      let teamsData = [];
      if (teamsResponse.data && teamsResponse.data.success) {
        teamsData = teamsResponse.data.teams || [];
        console.log("API - Teams:", JSON.stringify(teamsResponse.data, null, 2));

        if (teamsData.length > 0) {
          console.log("DETTAGLIO PRIMO TEAM:", JSON.stringify(teamsData[0], null, 2));

          if (teamsData[0].members && teamsData[0].members.length > 0) {
            console.log("DETTAGLIO PRIMO MEMBRO TEAM:", JSON.stringify(teamsData[0].members[0], null, 2));
            console.log("CAMPI DISPONIBILI MEMBRO:", Object.keys(teamsData[0].members[0]).join(", "));
          }
        }
      }

      // Extract team members and add them to participants list
      const teamMembers = [];
      teamsData.forEach(team => {
        if (team.members && Array.isArray(team.members)) {
          team.members.forEach(member => {
            // Add team information to each team member
            teamMembers.push({
              ...member,
              teamId: team._id,
              teamName: team.name,
              association_id: team.association_id,
              isTeamMember: true,
              registration_date: team.registration_date
            });
          });
        }
      });

      // Combine individual participants and team members
      setParticipants(participantsData);
      setTeams(teamsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching participants:', err);
      setError('Could not fetch participants list');
      setLoading(false);
    }
  }, [eventId]);

  // Initialize component
  useEffect(() => {
    fetchEventDetails();
    fetchAllAssociations();
    fetchParticipants();
  }, [fetchEventDetails, fetchAllAssociations, fetchParticipants]);

  // Helper function to get association info (name and fiscal code)
  const getAssociationInfo = (associationId) => {
    // Parse ID to ensure consistent comparison
    const parsedId = associationId?.toString() || '';

    // console.log(`Cercando associazione con ID: ${parsedId}`);

    // Look for association in the associations list
    const association = associations.find(a => a.id?.toString() === parsedId);

    if (association) {
      // console.log(`Associazione trovata:`, association);
      return {
        name: association.name || `Associazione ${parsedId}`,
        // Usa il formato richiesto per il codice fiscale (S310490)
        code: association.association_code || association.display_code
      };
    } else {
      console.log(`Associazione non trovata per ID: ${parsedId}`);
      // Return placeholders to indicate missing data
      return {
        name: `Associazione ${parsedId}`,
        code: undefined
      };
    }
  };  // Backward compatibility function
  const getAssociationName = (associationId) => {
    return getAssociationInfo(associationId).name;
  };

  // Handle participant status change
  const handleStatusChange = async (participantId, newStatus) => {
    try {
      if (!participantId) {
        console.error('Cannot update status: Missing participant ID');
        setError('Cannot update participant status: Missing ID');
        return;
      }

      const token = localStorage.getItem('token');

      await axiosInstance.patch(
        `/api/events/${eventId}/participants/${participantId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update state locally
      setParticipants(participants.map(p =>
        (p._id === participantId || p.id === participantId) ? { ...p, status: newStatus } : p
      ));
    } catch (err) {
      console.error('Error updating participant status:', err);
      setError('Failed to update participant status');
    }
  };

  // Handle removing a participant
  const handleRemoveParticipant = async (participantId) => {
    if (!participantId) {
      console.error('Cannot remove: Missing participant ID');
      setError('Cannot remove participant: Missing ID');
      return;
    }

    if (window.confirm('Are you sure you want to remove this participant?')) {
      try {
        const token = localStorage.getItem('token');

        await axiosInstance.delete(
          `/api/events/${eventId}/participants/${participantId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Remove participant from state
        setParticipants(participants.filter(p => (p._id !== participantId && p.id !== participantId)));
      } catch (err) {
        console.error('Error removing participant:', err);
        setError('Failed to remove participant');
      }
    }
  };

  // Handle adding a participant
  const handleAddParticipant = async (e) => {
    e.preventDefault();
    setModalError('');

    try {
      const token = localStorage.getItem('token');

      const response = await axiosInstance.post(
        `/api/events/${eventId}/participants`,
        newParticipant,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add new participant to state
      setParticipants([...participants, response.data]);
      setShowAddModal(false);
      setNewParticipant({
        name: '',
        email: '',
        phone: '',
        status: 'confirmed'
      });
    } catch (err) {
      console.error('Error adding participant:', err);
      setModalError(err.response?.data?.message || 'Failed to add participant');
    }
  };

  // Show team details modal
  const handleShowTeamDetails = (team) => {
    setSelectedTeam(team);
    setShowTeamDetailsModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewParticipant({
      ...newParticipant,
      [name]: value
    });
  };

  // Get all participants (both individual and team members) with filtering
  const getFilteredAndGroupedParticipants = () => {
    // Log participants data for debugging
    // console.log("Individual participants:", participants);
    // console.log("Teams data:", teams);

    // Combine individual participants and team members
    let allParticipants = [...participants];

    // Add team members to the participant list
    teams.forEach(team => {
      if (team.members && Array.isArray(team.members)) {
        team.members.forEach(member => {
          console.log("Team member data:", member); // Debug log
          allParticipants.push({
            id: member.member_id,
            _id: `team_${team._id}_member_${member.member_id}`,
            name: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
            first_name: member.first_name,
            last_name: member.last_name,
            email: member.email,
            fiscal_code: member.fiscal_code,
            association_id: team.association_id,
            association_name: getAssociationName(team.association_id),
            registration_date: team.registration_date,
            teamId: team._id,
            teamName: team.name,
            isTeamMember: true,
            formData: team.formData
          });
        });
      }
    });

    // Apply filtering
    let filteredParticipants = allParticipants.filter(participant => {
      // Status filter
      if (filterOption !== 'all' && participant.status !== filterOption) {
        return false;
      }

      // Search text filter
      const name = participant.name ||
        `${participant.first_name || ''} ${participant.last_name || ''}`.trim();
      const email = participant.email || '';
      const team = participant.teamName || '';
      const searchTermLower = searchTerm.toLowerCase();

      return name.toLowerCase().includes(searchTermLower) ||
        email.toLowerCase().includes(searchTermLower) ||
        team.toLowerCase().includes(searchTermLower);
    });

    // Group by selected grouping option
    if (groupBy === 'association') {
      // Group by association
      const grouped = {};

      filteredParticipants.forEach(participant => {
        const associationId = participant.association_id;
        const associationName = participant.association_name ||
          getAssociationName(associationId) ||
          `Association #${associationId}`;

        if (!grouped[associationId]) {
          grouped[associationId] = {
            id: associationId,
            name: associationName,
            participants: []
          };
        }

        grouped[associationId].participants.push(participant);
      });

      return Object.values(grouped);
    }
    else if (groupBy === 'team') {
      // Group by team (team members together, individual participants separate)
      const grouped = {};

      // First add teams
      teams.forEach(team => {
        grouped[team._id] = {
          id: team._id,
          name: team.name,
          association_id: team.association_id,
          association_name: getAssociationName(team.association_id),
          isTeam: true,
          formData: team.formData,
          participants: []
        };
      });

      // Add special group for individual participants
      grouped['individual'] = {
        id: 'individual',
        name: 'Individual Participants',
        isTeam: false,
        participants: []
      };

      // Place participants in appropriate groups
      filteredParticipants.forEach(participant => {
        if (participant.isTeamMember && participant.teamId && grouped[participant.teamId]) {
          grouped[participant.teamId].participants.push(participant);
        } else {
          grouped['individual'].participants.push(participant);
        }
      });

      // Remove empty groups
      Object.keys(grouped).forEach(key => {
        if (grouped[key].participants.length === 0) {
          delete grouped[key];
        }
      });

      return Object.values(grouped);
    }
    else if (groupBy === 'status') {
      // Group by status
      const grouped = {
        confirmed: { id: 'confirmed', name: 'Confirmed', participants: [] },
        pending: { id: 'pending', name: 'Pending', participants: [] },
        waitlist: { id: 'waitlist', name: 'Waitlist', participants: [] },
        cancelled: { id: 'cancelled', name: 'Cancelled', participants: [] },
        unknown: { id: 'unknown', name: 'Unknown Status', participants: [] }
      };

      filteredParticipants.forEach(participant => {
        const status = participant.status || 'unknown';
        if (grouped[status]) {
          grouped[status].participants.push(participant);
        } else {
          grouped['unknown'].participants.push(participant);
        }
      });

      // Remove empty groups
      Object.keys(grouped).forEach(key => {
        if (grouped[key].participants.length === 0) {
          delete grouped[key];
        }
      });

      return Object.values(grouped);
    }
    else {
      // No grouping, return all participants in a single group
      return [{
        id: 'all',
        name: 'All Participants',
        participants: filteredParticipants
      }];
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-success';
      case 'pending': return 'bg-warning';
      case 'cancelled': return 'bg-danger';
      case 'waitlist': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';

      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Funzione per esportare i partecipanti in formato XLSX (Excel)
  const exportToXLSX = () => {
    try {
      // Costruisci lista completa di partecipanti includendo i membri dei team
      const allParticipants = [...participants];
      teams.forEach(team => {
        if (team.members && Array.isArray(team.members)) {
          team.members.forEach(member => {
            if (!allParticipants.some(p => p.member_id === member.member_id)) {
              allParticipants.push({
                ...member,
                association_id: team.association_id,
                teamName: team.name
              });
            }
          });
        }
      });

      let missingDataCount = 0;

      // Prepara i dati per il foglio Excel
      const rows = allParticipants.map(participant => {
        const associationInfo = getAssociationInfo(participant.association_id);

        const firstName = participant.first_name || (participant.name ? participant.name.split(' ')[0] : '');
        const lastName = participant.last_name || (participant.name && participant.name.split(' ').length > 1 ? participant.name.substring(participant.name.indexOf(' ') + 1) : '');
        const associationCode = associationInfo?.code || '';
        const fiscalCode = participant.fiscal_code || participant.member_id || '';
        const teamName = participant.teamName || 'Individuale';

        const hasAllData = associationCode && fiscalCode && firstName && lastName;
        if (!hasAllData) missingDataCount++;

        return {
          'Codice Fiscale Associazione': associationCode,
          'Codice Fiscale': fiscalCode,
          'Nome': firstName,
          'Cognome': lastName,
          'Team': teamName
        };
      });

      if (missingDataCount > 0) {
        setError(`Attenzione: ${missingDataCount} partecipanti con dati incompleti sono stati inclusi nell'export XLSX.`);
      }

      const headerOrder = ['Codice Fiscale Associazione', 'Codice Fiscale', 'Nome', 'Cognome', 'Team'];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: headerOrder });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Partecipanti');

      // Scarica il file Excel
      XLSX.writeFile(workbook, `partecipanti-evento-${eventId}.xlsx`);
    } catch (error) {
      console.error('Error exporting XLSX:', error);
      setError('Errore durante l\'esportazione in XLSX');
    }
  };

  const groupedParticipants = getFilteredAndGroupedParticipants();
  const totalParticipants = participants.length + teams.reduce((count, team) =>
    count + (team.members ? team.members.length : 0), 0); return (
      <div className="container-fluid min-vh-100" style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
        <div className="row p-2 p-md-4">
          <div className="col-12">
            <div className="glass-container p-3 p-md-4 rounded-4" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
              {event ? (
                <div className="mb-4">
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <h1 style={{ color: '#006e39' }} className="mb-0 fs-4 fs-md-1">{event.title} - Partecipanti</h1>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        variant="outline-success"
                        onClick={() => navigate(`/admin/events/${eventId}/teams/manage`)}
                        style={{ borderColor: '#006e39', color: '#006e39' }}
                      >
                        <i className="bi bi-list-ol me-2"></i>
                        Gestione Scaletta                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => setShowAssocModal(true)}
                          style={{ backgroundColor: '#0d6efd', borderColor: '#0d6efd' }}
                        >
                          <i className="bi bi-person-plus-fill me-2"></i>
                          Iscrivi Associazione
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => navigate(`/admin/events/${eventId}/teams/documents`)}
                        style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
                      >
                        <i className="bi bi-file-earmark-zip me-2"></i>
                        Export Documenti
                      </Button>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <div className="card mb-3" style={{ borderColor: '#006e39', borderLeft: '4px solid #006e39' }}>
                        <div className="card-body">
                          <h5 className="card-title" style={{ color: '#006e39' }}>Partecipanti</h5>
                          <p className="card-text">{totalParticipants} / {event.maxParticipants}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card mb-3" style={{ borderColor: '#006e39', borderLeft: '4px solid #006e39' }}>
                        <div className="card-body">
                          <h5 className="card-title" style={{ color: '#006e39' }}>Stato</h5>
                          <p className="card-text">
                            {totalParticipants >= event.maxParticipants ? (
                              <span className="badge bg-danger">Completato</span>
                            ) : (
                              <span className="badge" style={{ backgroundColor: '#006e39' }}>Disponibile</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <h1 style={{ color: '#006e39' }} className="mb-4">Caricamento evento...</h1>
              )}

              {error && <div className="alert alert-danger">{error}</div>}

              <div className="mb-4">
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <div className="input-group input-group-lg">
                          <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                            <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            style={{ borderColor: '#006e39' }}
                            placeholder="Cerca per nome, email o squadra..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          {searchTerm && (
                            <button
                              className="btn btn-outline-danger"
                              type="button"
                              onClick={() => setSearchTerm('')}
                              title="Cancella ricerca"
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-md-3 mb-2">
                        <Dropdown>
                      <Dropdown.Toggle
                        variant="default"
                        id="dropdown-group"
                        style={{ borderColor: '#006e39', color: '#006e39', width: '100%' }}
                      >
                        <i className="bi bi-collection"></i> {' '}
                        {groupBy === 'association' ? 'Raggruppa per associazione' :
                          groupBy === 'team' ? 'Raggruppa per squadra' : 'Senza raggruppamento'}
                      </Dropdown.Toggle>

                      <Dropdown.Menu style={{ width: '100%' }}>
                        <Dropdown.Item onClick={() => setGroupBy('association')}>
                          Raggruppa per associazione
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setGroupBy('team')}>
                          Raggruppa per squadra
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setGroupBy('status')}>
                          Raggruppa per stato
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setGroupBy('none')}>
                          Senza raggruppamento
                        </Dropdown.Item>
                      </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center my-5">
                  <div className="spinner-border" style={{ color: '#006e39' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2" style={{ color: '#006e39' }}>Caricamento partecipanti...</p>
                </div>
              ) : groupedParticipants.length === 0 ? (
                <div className="alert" style={{ backgroundColor: '#eef8f3', color: '#006e39', borderColor: '#006e39' }}>
                  <i className="bi bi-info-circle me-2"></i>
                  {searchTerm ? 'Nessun partecipante corrisponde alla ricerca.' : 'Nessun partecipante registrato per questo evento.'}
                </div>
              ) : (
                <div className="mb-4">
                  {groupedParticipants.map((group) => (
                    <div key={group.id} className="card mb-4" style={{ borderColor: '#006e39', borderLeft: '4px solid #006e39' }}>
                      <div className="card-header d-flex justify-content-between align-items-center"
                        style={{ backgroundColor: '#eef8f3', color: '#006e39', borderColor: '#006e39' }}>
                        <h5 className="mb-0">
                          {group.isTeam ? (
                            <>
                              <i className="bi bi-people-fill me-2"></i>
                              {group.name}
                              <span className="ms-2 small text-muted">
                                ({getAssociationName(group.association_id)})
                              </span>
                            </>
                          ) : (
                            <>
                              {groupBy === 'association' ? (
                                <i className="bi bi-building me-2"></i>
                              ) : groupBy === 'status' ? (
                                <i className="bi bi-tag me-2"></i>
                              ) : (
                                <i className="bi bi-person me-2"></i>
                              )}
                              {group.name}
                            </>
                          )}
                        </h5>
                        <div>
                          <span className="badge bg-secondary">{group.participants.length} partecipanti</span>
                          {group.isTeam && (
                            <button
                              className="btn btn-sm ms-2"
                              style={{ backgroundColor: '#006e39', color: 'white' }}
                              onClick={() => {
                                const team = teams.find(t => t._id === group.id);
                                if (team) handleShowTeamDetails(team);
                              }}
                            >
                              <i className="bi bi-info-circle"></i> Dettagli
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="card-body p-0">
                        {group.participants.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-hover mb-0">
                              <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                  <th>Nome</th>
                                  <th>Cognome</th>
                                  <th>Codice Fiscale</th>
                                  <th>Data Iscrizione</th>
                                  <th>Azioni</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.participants.map(participant => (
                                  <tr
                                    key={participant._id || participant.id || Math.random().toString()}
                                    className={!participant.first_name || !participant.last_name || !participant.fiscal_code ? 'table-danger' : ''}
                                  >
                                    <td>
                                      {participant.first_name ||
                                        (participant.name ?
                                          participant.name.split(' ')[0] :
                                          <span className="text-danger">Mancante</span>
                                        )
                                      }
                                      {participant.isTeamMember && participant.teamName && (
                                        <span className="badge ms-2" style={{ backgroundColor: '#006e39' }}>
                                          <i className="bi bi-people-fill me-1"></i> {participant.teamName}
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      {participant.last_name ||
                                        (participant.name && participant.name.split(' ').length > 1 ?
                                          participant.name.split(' ').slice(1).join(' ') :
                                          <span className="text-danger">Mancante</span>
                                        )
                                      }
                                    </td>
                                    <td>
                                      {participant.fiscal_code ||
                                        participant.member_id ||
                                        <span className="text-danger">Mancante</span>
                                      }
                                    </td>
                                    <td>{formatDate(participant.registrationDate || participant.registration_date)}</td>
                                    <td>
                                      <button
                                        className="btn btn-sm"
                                        style={{ borderColor: '#dc3545', color: '#dc3545' }}
                                        onClick={() => handleRemoveParticipant(participant._id || participant.id)}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="p-3 text-center text-muted">
                            Nessun partecipante in questo gruppo.
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tabella completa con tutti i partecipanti */}
              <div className="mt-5">
                <hr />
                <h3 style={{ color: '#006e39' }} className="mb-3">
                  Tutti i partecipanti <span className="badge bg-secondary">{totalParticipants}</span>
                </h3>
                {totalParticipants > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th>Codice Fiscale Associazione</th>
                          <th>Codice Fiscale</th>
                          <th>Nome</th>
                          <th>Cognome</th>
                          <th>Team</th>
                          <th>Data Iscrizione</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participants.map((participant, index) => {
                          // Get association info
                          const associationInfo = getAssociationInfo(participant.association_id);

                          // Check for missing required data
                          const hasMissingData =
                            !associationInfo?.code ||
                            !participant.fiscal_code ||
                            !participant.first_name ||
                            !participant.last_name;

                          return (
                            <tr key={`participant-${index}`} className={hasMissingData ? 'table-danger' : ''}>
                              <td>{associationInfo?.code || <span className="text-danger">Mancante</span>}</td>
                              <td>{participant.fiscal_code || <span className="text-danger">Mancante</span>}</td>
                              <td>{participant.first_name || <span className="text-danger">Mancante</span>}</td>
                              <td>{participant.last_name || <span className="text-danger">Mancante</span>}</td>
                              <td>{participant.teamName || 'Individuale'}</td>
                              <td>{formatDate(participant.registration_date)}</td>
                            </tr>
                          );
                        })}

                        {teams.flatMap((team, teamIndex) =>
                          team.members && Array.isArray(team.members)
                            ? team.members.map((member, memberIndex) => {
                              // Get association info
                              const associationInfo = getAssociationInfo(team.association_id);

                              // Check for missing required data
                              const hasMissingData =
                                !associationInfo?.code ||
                                !member.fiscal_code ||
                                !member.first_name ||
                                !member.last_name ||
                                !team.name;

                              return (
                                <tr key={`team-${teamIndex}-member-${memberIndex}`} className={hasMissingData ? 'table-danger' : ''}>
                                  <td>{associationInfo?.code || <span className="text-danger">Mancante</span>}</td>
                                  <td>{member.fiscal_code || <span className="text-danger">Mancante</span>}</td>
                                  <td>{member.first_name || <span className="text-danger">Mancante</span>}</td>
                                  <td>{member.last_name || <span className="text-danger">Mancante</span>}</td>
                                  <td>{team.name || <span className="text-danger">Mancante</span>}</td>
                                  <td>{formatDate(team.registration_date)}</td>
                                </tr>
                              );
                            })
                            : []
                        )}
                      </tbody>
                    </table>
                    {participants.some(p => !p.fiscal_code || !p.first_name || !p.last_name) ||
                      teams.some(t => t.members && t.members.some(m => !m.fiscal_code || !m.first_name || !m.last_name)) ? (
                      <div className="alert alert-danger mt-3">
                        <strong>Attenzione:</strong> Alcuni partecipanti hanno dati mancanti.
                        Clicca sul pulsante "Sincronizza Dati" in fondo alla pagina per recuperare le informazioni mancanti dal database principale.
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="alert alert-info">Nessun partecipante registrato.</div>
                )}
              </div>

              <div className="mt-4 d-flex gap-2">
                <button
                  className="btn"
                  style={{ backgroundColor: '#006e39', color: 'white' }}
                  onClick={() => navigate('/admin/events/list')}
                >
                  <i className="bi bi-arrow-left"></i> Torna alla lista eventi
                </button>
                <button
                  className="btn"
                  style={{ backgroundColor: '#004e29', color: 'white' }}
                  onClick={exportToXLSX}
                  disabled={totalParticipants === 0}
                >
                  <i className="bi bi-file-earmark-spreadsheet"></i> Esporta XLSX
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Selezione Associazione per Iscrizione */}
        <Modal show={showAssocModal} onHide={() => { setShowAssocModal(false); setSelectedAssoc(''); setAssocSearchTerm(''); }} centered>
          <Modal.Header closeButton>
            <Modal.Title>Iscrivi per Conto di una Associazione</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Scegli Associazione</label>
              <input 
                type="text" 
                className="form-control mb-2" 
                placeholder="Cerca per codice o nome..." 
                value={assocSearchTerm}
                onChange={(e) => setAssocSearchTerm(e.target.value)}
              />
              <select 
                className="form-select" 
                value={selectedAssoc} 
                onChange={(e) => setSelectedAssoc(e.target.value)}
                size="5"
              >
                <option value="">-- Seleziona una Associazione --</option>
                {associations && associations
                  .filter(assoc => 
                    !assocSearchTerm || 
                    (assoc.name?.toLowerCase().includes(assocSearchTerm.toLowerCase())) || 
                    (assoc.code?.toLowerCase().includes(assocSearchTerm.toLowerCase()))
                  )
                  .map((assoc) => (
                  <option key={assoc.id} value={assoc.id}>
                    {assoc.code} - {assoc.name}
                  </option>
                ))}
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAssocModal(false)}>
              Annulla
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => {
                if (selectedAssoc) {
                  navigate(`/admin/events/${eventId}/register/${selectedAssoc}`);
                }
              }}
              disabled={!selectedAssoc}
            >
              <i className="bi bi-person me-2"></i>Iscritti Singoli
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (selectedAssoc) {
                  navigate(`/admin/events/${eventId}/team-registration/${selectedAssoc}`);
                }
              }}
              disabled={!selectedAssoc}
            >
              <i className="bi bi-people me-2"></i>Iscrizione Squadre
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
};

export default EventParticipants;







